import { useQuery } from '@apollo/client';

import {
  DashboardGranularity,
  DashboardPaymentMethod,
  DashboardTeamMembersValues,
  DashboardTransaction,
  MerchantStats,
} from '__generated__/GQL';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';

import useCompanyContext from '../../../../../common/hooks/useCompanyContext';
import { formatUTCEndOfDay, formatUTCStartOfDay } from '../../../common';
import { QueryDashboardDocument } from './queryDashboard.gql';

const cumulativeTransactionData = (
  data: DashboardTransaction[] | undefined,
): DashboardTransaction[] | undefined => {
  if (!data) {
    return undefined;
  }

  const cumulativeData: DashboardTransaction[] = [];

  data?.forEach((item, index) => {
    const newValue = item?.newValue;
    const oldValue = item?.oldValue;

    if (index === 0) {
      cumulativeData.push({
        ...item,
        newDate: item.newDate,
        newValue,
        oldDate: item.oldDate,
        oldValue,
      });
    } else {
      const previousIndex = cumulativeData[index - 1];
      cumulativeData.push({
        ...item,
        newDate: item.newDate,
        newValue:
          // @ts-expect-error previousIndex is possibly 'undefined'.
          newValue !== null ? (previousIndex.newValue ?? 0) + newValue : null,
        oldDate: item.oldDate,
        oldValue:
          // @ts-expect-error previousIndex is possibly 'undefined'.
          oldValue !== null ? (previousIndex.oldValue ?? 0) + oldValue : null,
      });
    }
  });

  return cumulativeData;
};

export const useDashboardQuery = ({
  endDate,
  previousEndDate,
  previousStartDate,
  startDate,
}: {
  startDate: Date;
  endDate: Date;
  previousStartDate?: Date;
  previousEndDate?: Date;
}) => {
  const { companyProfileId } = useCompanyContext();
  const { mainBankAccountId } = useMainBankAccountId();
  const { data, error, loading } = useQuery(QueryDashboardDocument, {
    skip: !mainBankAccountId,
    variables: {
      dashboardInput: {
        bankAccountId: mainBankAccountId as string,
        companyProfileId: companyProfileId as string,
        // Sending ISO dates can cause issues because of the way backend handles dates and is expecting UTC 0 ones.
        // If we are in a different timezone, let's say UTC+1, we could fetch the wrong dates by sending dates such as:
        // 2023-11-30T23:00:00 when we would want December 1st.
        // Until we harmonize everything between backend and frontend, we always fetch data in UTC0 days, no matter the local timezone.
        endDate: formatUTCEndOfDay(endDate),
        previousEndDate: previousEndDate
          ? formatUTCEndOfDay(previousEndDate)
          : undefined,
        previousStartDate: previousStartDate
          ? formatUTCStartOfDay(previousStartDate)
          : undefined,
        startDate: formatUTCStartOfDay(startDate),
      },
    },
  });

  const newPayinTotal = data?.cashflowManagementDashboard?.payin?.reduce(
    (acc, value) => {
      if (!value || !value.newValue) {
        return acc;
      }

      return acc + value.newValue;
    },
    0,
  );
  const oldPayinTotal = data?.cashflowManagementDashboard?.payin?.reduce(
    (acc, value) => {
      if (!value || !value.oldValue) {
        return acc;
      }

      return acc + value.oldValue;
    },
    0,
  );
  const newPayoutTotal = data?.cashflowManagementDashboard?.payout?.reduce(
    (acc, value) => {
      if (!value || !value.newValue) {
        return acc;
      }

      return acc + value.newValue;
    },
    0,
  );
  const oldPayoutTotal = data?.cashflowManagementDashboard?.payout?.reduce(
    (acc, value) => {
      if (!value || !value.oldValue) {
        return acc;
      }

      return acc + value.oldValue;
    },
    0,
  );

  const balance = data?.cashflowManagementDashboard?.balance as
    | DashboardTransaction[]
    | undefined;
  let newBalanceTotal = 0;
  let oldBalanceTotal = 0;
  balance?.forEach((b) => {
    if (b.newValue !== null) {
      newBalanceTotal = b.newValue;
    }

    if (b.oldValue !== null) {
      oldBalanceTotal = b.oldValue;
    }
  });

  const payin = data?.cashflowManagementDashboard?.payin as
    | DashboardTransaction[]
    | undefined;
  const payout = data?.cashflowManagementDashboard?.payout as
    | DashboardTransaction[]
    | undefined;

  const cumulativePayin = cumulativeTransactionData(payin);
  const cumulativePayout = cumulativeTransactionData(payout);

  return {
    balance,
    clients: data?.cashflowManagementDashboard?.clients,
    cumulativePayin,
    cumulativePayout,
    error,
    granularity:
      data?.cashflowManagementDashboard?.granularity ??
      DashboardGranularity.Day,
    loading,
    merchants: data?.cashflowManagementDashboard?.merchants as
      | MerchantStats[]
      | undefined,
    newBalanceTotal,
    newPayinTotal,
    newPayoutTotal,
    oldBalanceTotal,
    oldPayinTotal,
    oldPayoutTotal,
    payin,
    paymentMethod: data?.cashflowManagementDashboard?.paymentMethod as
      | DashboardPaymentMethod
      | undefined,
    payout,
    teamMembers: data?.cashflowManagementDashboard?.teamMembers as
      | DashboardTeamMembersValues
      | undefined,
  };
};
